import React, { useEffect, useRef } from 'react';
import { List, useSafeArea } from '@banhala/ui-kit';
import { FlatList as GHFlatList } from 'react-native-gesture-handler';
import { useGoodsLikeRecommend } from '../queries/useGoodsLikeRecommend';
import { useLogDisplayCarousel } from '../hooks/useLogDisplayCarousel';
import { GoodsCarouselItem } from './GoodsCarouselItem';
import { GoodsRecommendSkeleton } from './GoodsRecommendSkeleton';
import { isAndroid } from 'shared/src/utils';

// 바텀시트(RNGH Pan) → 세로 리스트 → 가로 GoodsCarousel(Android: RNGH GHFlatList) 로
// 중첩되는데, 세로 리스트가 순수 RN FlatList 면 RNGH 제스처 체인이 끊겨 Android 에서
// 안쪽 가로 스크롤이 동작하지 않는다. Android 는 세로 리스트도 RNGH FlatList 로 통일한다.
// (iOS 는 네이티브가 중첩 스크롤을 처리하므로 기존 List 유지)
const ScrollableList = (isAndroid ? GHFlatList : List) as typeof List;
type GoodsLikeRecommendProps = {
  isExpanded: boolean;
  onContentHeightChange: (size: number) => void;
  onCarouselCountChange?: (count: number) => void;
};

const GoodsLikeRecommendContent: React.FC<GoodsLikeRecommendProps> = props => {
  const { data: goodsLikeRecommend } = useGoodsLikeRecommend();
  const { bottom } = useSafeArea();

  const { onViewableItemsChanged, viewedIndices } = useLogDisplayCarousel();
  const viewabilityConfigRef = useRef({
    itemVisiblePercentThreshold: isAndroid ? 20 : 80,
  });

  const carouselCount = goodsLikeRecommend?.carousels.length ?? 0;
  const onCarouselCountChange = props.onCarouselCountChange;
  useEffect(() => {
    onCarouselCountChange?.(carouselCount);
  }, [carouselCount, onCarouselCountChange]);

  return (
    <ScrollableList
      style={{ flex: 1 }}
      data={goodsLikeRecommend?.carousels}
      onContentSizeChange={(contentWidth, contentHeight) => {
        props.onContentHeightChange(contentHeight);
      }}
      contentContainerStyle={{
        paddingBottom: bottom,
      }}
      scrollEnabled={props.isExpanded}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={viewabilityConfigRef.current}
      renderItem={({ item: carousel, index }) => {
        return (
          <GoodsCarouselItem
            carousel={carousel}
            isLogEnabled={viewedIndices.has(index)}
          />
        );
      }}
    />
  );
};

export const GoodsLikeRecommend: React.FC<GoodsLikeRecommendProps> = props => {
  return (
    <React.Suspense fallback={<GoodsRecommendSkeleton />}>
      <GoodsLikeRecommendContent {...props} />
    </React.Suspense>
  );
};
